import { MeiucaService as BaseMeiucaService } from '@meiuca/service'
import { env, apiEndpoints } from '@/lib/env'
import type { InternalAxiosRequestConfig } from 'axios'
import { userManager } from '@/managers/userManager'

// Extended MeiucaService with user preferences support
class MeiucaAppService extends BaseMeiucaService {
  constructor(config: any) {
    super(config)
    this.setupUserPreferencesInterceptor()
  }

  private setupUserPreferencesInterceptor() {
    // Add interceptor to inject user preferences header
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        this.addUserPreferencesHeader(config)
        return config
      },
      (error) => Promise.reject(error)
    )
  }

  private addUserPreferencesHeader(config: InternalAxiosRequestConfig): void {
    try {
      // Only add preferences header in browser environment
      if (typeof window === 'undefined') {
        return
      }

      // Use userManager instead of direct localStorage access
      const prefs = userManager.getPreferences()
      if (prefs && prefs.modalities && prefs.modalities.length > 0) {
        // Encode preferences as base64 JSON for backend
        const preferencesJson = JSON.stringify({ modalities: prefs.modalities })
        const base64Preferences = btoa(preferencesJson)
        config.headers['X-User-Preferences'] = base64Preferences
      }
    } catch (error) {
      // Silently fail - preferences are optional
      console.debug('[MeiucaAppService] Failed to add user preferences header:', error)
    }
  }

  // Public methods for token management
  public clearTokens(): void {
    this.tokenStorage.clearTokens()
  }

  public getAccessToken(): string | undefined {
    return this.tokenStorage.getAccessToken()
  }
}

// Create configured instance of MeiucaService for this app
export const meiucaService = new MeiucaAppService({
  baseURL: env.API_URL,
  refreshEndpoint: apiEndpoints.auth.refresh,
  cookiePrefix: '', // No prefix for meiuca-app (keeps original behavior)
  debug: env.IS_DEVELOPMENT,
  onAuthenticationError: () => {
    // Use replace to prevent back button issues - redirect to root
    if (typeof window !== 'undefined') {
      window.location.replace('/')
    }
  }
})


// Generic API response type for consistent API response structure
export interface ApiResponse<T> {
  data: T
}

// Re-export the class for services that need to extend it
export { MeiucaService as BaseMeiucaService } from '@meiuca/service'

// Legacy export for backward compatibility
export { MeiucaService } from '@meiuca/service'
